import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExcelJS from 'exceljs';
import CustomDatePicker from '../form-components/CustomDatePicker';
import { getTransactions, getAdminEntities } from '../../api/paymentApi';
import { dropdownMenuProps, selectFieldSx } from '../../utils/dropdownStyles';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { useSpaceId } from '../../hooks/useSpaceId';
import { useToast } from '../../context/ToastContext';

const isRefundedStatus = (s) => s === 'Partially Refunded' || s === 'Fully Refunded';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
// Safety ceiling for a single export fetch — the 6-month window is the real
// guard rail; this is just a backstop so a request can never be unbounded.
const MAX_EXPORT_ROWS = 100000;

const GATEWAY_OPTIONS = ['Stripe', 'PayPal'];

const STATUS_CONFIG = {
  Succeeded:            { bg: '#dcfce7', color: '#15803d' },
  Pending:              { bg: '#fef3c7', color: '#d97706' },
  Cancelled:            { bg: '#fee2e2', color: '#dc2626' },
  'Partially Refunded': { bg: '#ede9fe', color: '#7c3aed' },
  'Fully Refunded':     { bg: '#f3e8ff', color: '#6d28d9' },
};

const GATEWAY_CONFIG = {
  Stripe: { bg: '#eef2ff', color: '#4f46e5' },
  PayPal: { bg: '#dbeafe', color: '#1d4ed8' },
};

const TXN_TYPE_CONFIG = {
  Purchase: { bg: '#eff6ff', color: '#1d4ed8' },
  Refund:   { bg: '#fdf2f8', color: '#be185d' },
};

const STATUS_OPTIONS   = Object.keys(STATUS_CONFIG);
const TXN_TYPE_OPTIONS = Object.keys(TXN_TYPE_CONFIG);

const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$' };

// Excel export styling
const EXCEL_BRAND  = 'FF0A326D';
const EXCEL_BORDER = 'FFE2E8F0';
const EXCEL_ZEBRA   = 'FFF8FAFC';
const EXCEL_TEXT    = 'FF1E293B';
const EXCEL_SUBTLE  = 'FF64748B';
const toArgb = (hex) => `FF${hex.replace('#', '').toUpperCase()}`;

const formatAmount = (amount, currency) =>
  `${CURRENCY_SYMBOLS[currency] ?? currency}${Number(amount).toFixed(2)}`;

const formatDate = (dateString) =>
  new Date(dateString).toLocaleString('en-US', {
    month: 'short', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? { bg: '#f1f5f9', color: '#64748b' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '999px', bgcolor: cfg.bg, fontSize: 11, fontWeight: 700, color: cfg.color }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
      <LocalText>{status}</LocalText>
    </Box>
  );
};

const GatewayBadge = ({ gateway }) => {
  const cfg = GATEWAY_CONFIG[gateway] ?? { bg: '#f1f5f9', color: '#64748b' };
  return (
    <Box sx={{ display: 'inline-block', px: 1.25, py: 0.4, borderRadius: '8px', bgcolor: cfg.bg, fontSize: 11, fontWeight: 700, color: cfg.color }}>
      {gateway}
    </Box>
  );
};

const TxnTypeBadge = ({ type }) => {
  const cfg = TXN_TYPE_CONFIG[type] ?? { bg: '#f1f5f9', color: '#64748b' };
  return (
    <Box sx={{ display: 'inline-block', px: 1.25, py: 0.4, borderRadius: '8px', bgcolor: cfg.bg, fontSize: 11, fontWeight: 700, color: cfg.color }}>
      <LocalText>{type}</LocalText>
    </Box>
  );
};

// Base columns always shown
const BASE_COLUMNS = [
  { key: 'id',           label: 'Payment Intent ID',   align: 'left'  },
  { key: 'transactionType', label: 'Transaction Type', align: 'left' },
  { key: 'status',       label: 'Status',               align: 'left'  },
  { key: 'customer',     label: 'Customer Email / ID',  align: 'left'  },
  { key: 'customerName', label: 'Customer Name',        align: 'left'  },
  { key: 'gateway',      label: 'Gateway',              align: 'left'  },
  { key: 'method',       label: 'Payment Method',       align: 'left'  },
  { key: 'date',         label: 'Date',                 align: 'left'  },
  { key: 'currency',     label: 'Currency',             align: 'left'  },
  { key: 'charged',      label: 'Charged Amt',          align: 'right' },
  { key: 'paid',         label: 'Paid / Refund Amt',    align: 'right' },
  { key: 'fee',          label: 'Gateway Fee',          align: 'right' },
  { key: 'fxRate',       label: 'FX Rate',              align: 'right' },
  { key: 'contractPct',  label: 'Contract %',           align: 'right' },
  { key: 'netAmount',    label: 'Net Amount',           align: 'right' },
  { key: 'merchantNet',  label: 'Settled Amount',       align: 'right' },
  { key: 'depositId',    label: 'Deposit ID',           align: 'left'  },
  { key: 'refund',       label: 'Refund Reason',        align: 'left'  },
];

// Extra columns shown only in the global (gms_accountant) view
const GLOBAL_COLUMNS = [
  { key: 'entity',   label: 'Entity',    align: 'left' },
  { key: 'entityId', label: 'Entity ID', align: 'left' },
  { key: 'space',    label: 'Space',     align: 'left' },
  { key: 'spaceId',  label: 'Space ID',  align: 'left' },
];

const multiSelectSx = {
  ...selectFieldSx,
  fontSize: 13,
  minWidth: 180,
  maxWidth: 260,
  '& .MuiSelect-select': { py: '6.5px' },
};

const ENTITY_SPACE_SELECT_W = 260;
const entitySpaceSelectSx = {
  ...multiSelectSx,
  minWidth: 0,
  maxWidth: 'none',
  width: { xs: '100%', sm: ENTITY_SPACE_SELECT_W },
  '& .MuiSelect-select': {
    py: '6.5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
const entitySpaceMenuProps = {
  ...dropdownMenuProps,
  PaperProps: {
    ...dropdownMenuProps.PaperProps,
    sx: { ...dropdownMenuProps.PaperProps?.sx, width: { sm: ENTITY_SPACE_SELECT_W } },
  },
};

// Status/Gateway/Type sit in a 2-column grid on mobile (see the filter-row markup below),
// so on xs they need to fill their grid cell instead of multiSelectSx's fixed 180-260px
// range, which would overflow a ~170px cell. sm+ keeps multiSelectSx's own values exactly
// (the grid becomes `display: contents` there, so these render as plain flex items again).
const compactFilterSelectSx = {
  ...multiSelectSx,
  width: { xs: '100%', sm: 'auto' },
  minWidth: { xs: 0, sm: 180 },
  maxWidth: { xs: 'none', sm: 260 },
};

const TransactionTable = ({ fetchFn = getTransactions, showGlobalFilters = false }) => {
  const spaceId = useSpaceId();
  const [transactions, setTransactions] = useState([]);
  const [totalCount,   setTotalCount]   = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [exporting,    setExporting]    = useState(false);
  const { toast } = useToast();

  // Search has a debounce — searchInput is what the user types,
  // search is the debounced value sent to the API
  const [searchInput,   setSearchInput]   = useState('');
  const [search,        setSearch]        = useState('');
  const [selectedStatuses,         setSelectedStatuses]         = useState([]);
  const [selectedGateways,         setSelectedGateways]         = useState([]);
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState([]);
  const [dateFrom,      setDateFrom]      = useState('');
  const [dateTo,        setDateTo]        = useState('');
  // Only the committed range drives the API fetch
  const [committedDateRange, setCommittedDateRange] = useState({ from: '', to: '' });
  const [page,          setPage]          = useState(0);
  const [pageSize,      setPageSize]      = useState(PAGE_SIZE_OPTIONS[0]);

  // Global filters — only used when showGlobalFilters=true (gms_accountant)
  const [entities,          setEntities]          = useState([]);
  const [loadingEntities,   setLoadingEntities]   = useState(false);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [selectedSpaceIds,  setSelectedSpaceIds]  = useState([]);

  // Export modal — filters here are completely independent from the main
  // table's live filters above (search/selectedStatuses/etc.). Adjusting
  // them never changes what's shown in the table behind the dialog.
  const [exportModalOpen,        setExportModalOpen]        = useState(false);
  const [exportDateFrom,         setExportDateFrom]         = useState('');
  const [exportDateTo,           setExportDateTo]           = useState('');
  const [exportStatuses,         setExportStatuses]         = useState([]);
  const [exportGateways,         setExportGateways]         = useState([]);
  const [exportTransactionTypes, setExportTransactionTypes] = useState([]);
  const [exportEntityIds,        setExportEntityIds]        = useState([]);
  const [exportSpaceIds,         setExportSpaceIds]         = useState([]);

  // Localized non-JSX strings
  const searchPlaceholder = useLocalText('Search customer, ID...');

  const isDateRangeError = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  // Load entities once when the global filter panel is shown
  useEffect(() => {
    if (!showGlobalFilters) return;
    setLoadingEntities(true);
    getAdminEntities()
      .then((data) => setEntities(Array.isArray(data) ? data : []))
      .catch(() => setEntities([]))
      .finally(() => setLoadingEntities(false));
  }, [showGlobalFilters]);

  // Spaces available for the space multi-select (filtered by selected entities)
  const availableSpaces = useMemo(() => {
    if (!entities.length) return [];
    const pool = selectedEntityIds.length
      ? entities.filter((e) => selectedEntityIds.includes(e.id))
      : entities;
    return pool.flatMap((e) => e.spaces ?? []);
  }, [entities, selectedEntityIds]);

  // When the available spaces change, drop any selected spaces that no longer exist
  useEffect(() => {
    if (!selectedSpaceIds.length) return;
    const availableIds = availableSpaces.map((s) => s.id);
    setSelectedSpaceIds((prev) => prev.filter((id) => availableIds.includes(id)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSpaces]);

  // What to send to the API:
  // - spaces selected  → pass spaceIds (most specific)
  // - entities only    → pass entityIds (backend resolves to their spaces in SQL)
  // - nothing selected → pass neither (all data)
  const apiFilterParams = useMemo(() => {
    if (selectedSpaceIds.length > 0)  return { spaceIds: selectedSpaceIds };
    if (selectedEntityIds.length > 0) return { entityIds: selectedEntityIds };
    return {};
  }, [selectedSpaceIds, selectedEntityIds]);

  // Debounce search input (400 ms)
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 0 when any committed filter (or the page size) changes
  useEffect(() => {
    setPage(0);
  }, [search, selectedStatuses, selectedGateways, selectedTransactionTypes, committedDateRange, apiFilterParams, pageSize]);

  // Current filters as API params, shared between the paginated fetch and the full export fetch
  const buildFilterParams = useCallback(() => ({
    search:          search                         || undefined,
    status:          selectedStatuses.length         ? selectedStatuses         : undefined,
    gateway:         selectedGateways.length         ? selectedGateways         : undefined,
    transactionType: selectedTransactionTypes.length ? selectedTransactionTypes : undefined,
    dateFrom:        committedDateRange.from         || undefined,
    dateTo:          committedDateRange.to           || undefined,
    ...(showGlobalFilters ? apiFilterParams : {}),
  }), [search, selectedStatuses, selectedGateways, selectedTransactionTypes, committedDateRange, showGlobalFilters, apiFilterParams]);

  // Fetch from server whenever page, page size, or any committed filter changes
  const fetchTransactions = useCallback(() => {
    if (!spaceId && !showGlobalFilters) return;
    setLoading(true);
    fetchFn({
      page,
      pageSize,
      ...buildFilterParams(),
    })
      .then((data) => {
        setTransactions(Array.isArray(data) ? data : data?.items ?? []);
        setTotalCount(data?.totalCount ?? 0);
      })
      .catch(() => {
        setTransactions([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [spaceId, showGlobalFilters, fetchFn, page, pageSize, buildFilterParams]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleDateFromChange = (val) => {
    setDateFrom(val);
    if (!val && !dateTo) setCommittedDateRange({ from: '', to: '' });
  };

  const handleDateToChange = (val) => {
    setDateTo(val);
    if (dateFrom && val && dateFrom <= val) {
      setCommittedDateRange({ from: dateFrom, to: val });
    } else if (!val) {
      setCommittedDateRange({ from: '', to: '' });
    }
  };

  const resetFilters = () => {
    setSearchInput('');
    setSearch('');
    setSelectedStatuses([]);
    setSelectedGateways([]);
    setSelectedTransactionTypes([]);
    setDateFrom('');
    setDateTo('');
    setCommittedDateRange({ from: '', to: '' });
    setSelectedEntityIds([]);
    setSelectedSpaceIds([]);
    setPage(0);
  };

  const downloadExcel = async (rows) => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'GMS Content Management';
    workbook.created = new Date();

    const sheet = workbook.addWorksheet('Transactions');

    const columns = [
      ...(showGlobalFilters ? [
        { key: 'entity', width: 20 }, { key: 'entityId', width: 12 },
        { key: 'space', width: 20 },  { key: 'spaceId', width: 12 },
      ] : []),
      { key: 'id',              width: 24 },
      { key: 'transactionType', width: 16 },
      { key: 'status',          width: 18 },
      { key: 'customer',        width: 26 },
      { key: 'customerName',    width: 22 },
      { key: 'gateway',         width: 12 },
      { key: 'method',          width: 16 },
      { key: 'date',            width: 20 },
      { key: 'currency',        width: 10 },
      { key: 'charged',         width: 14 },
      { key: 'paid',            width: 16 },
      { key: 'fee',             width: 13 },
      { key: 'fxRate',          width: 12 },
      { key: 'contractPct',     width: 12 },
      { key: 'netAmount',           width: 14 },
      { key: 'merchantNet',         width: 16 },
      { key: 'merchantNetCurrency', width: 12 },
      { key: 'depositId',       width: 16 },
      { key: 'refund',          width: 26 },
    ];
    sheet.columns = columns;
    const colCount = columns.length;
    const colIndex = {};
    columns.forEach((c, i) => { colIndex[c.key] = i + 1; });

    // Title + subtitle banner
    sheet.mergeCells(1, 1, 1, colCount);
    const titleCell = sheet.getCell(1, 1);
    titleCell.value = 'Transaction Report';
    titleCell.font = { bold: true, size: 15, color: { argb: EXCEL_BRAND } };
    titleCell.alignment = { vertical: 'middle' };
    sheet.getRow(1).height = 26;

    sheet.mergeCells(2, 1, 2, colCount);
    const subtitleCell = sheet.getCell(2, 1);
    subtitleCell.value = `Generated ${new Date().toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}  ·  ${rows.length} transaction${rows.length !== 1 ? 's' : ''}`;
    subtitleCell.font = { italic: true, size: 10, color: { argb: EXCEL_SUBTLE } };
    sheet.getRow(2).height = 18;

    // Header row
    const HEADER_ROW = 4;
    const HEADER_LABELS = [
      ...(showGlobalFilters ? ['Entity', 'Entity ID', 'Space', 'Space ID'] : []),
      'Payment Intent ID', 'Transaction Type', 'Status', 'Customer Email / ID', 'Customer Name', 'Gateway', 'Payment Method',
      'Date', 'Currency', 'Charged Amt', 'Paid / Refund Amt', 'Gateway Fee', 'FX Rate', 'Contract %',
      'Net Amount', 'Settled Amount', 'Settled Amount Currency', 'Deposit ID', 'Refund Reason',
    ];
    const headerRow = sheet.getRow(HEADER_ROW);
    HEADER_LABELS.forEach((label, i) => { headerRow.getCell(i + 1).value = label; });
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 10.5, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_BRAND } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: EXCEL_BRAND } }, bottom: { style: 'thin', color: { argb: EXCEL_BRAND } },
        left: { style: 'thin', color: { argb: EXCEL_BRAND } }, right: { style: 'thin', color: { argb: EXCEL_BRAND } },
      };
    });
    headerRow.height = 30;

    const RIGHT_ALIGN_KEYS = ['charged', 'paid', 'fee', 'fxRate', 'contractPct', 'netAmount', 'merchantNet'];

    rows.forEach((tx, i) => {
      const row = sheet.getRow(HEADER_ROW + 1 + i);
      const values = {
        ...(showGlobalFilters ? {
          entity: tx.entityName ?? '', entityId: tx.entityId ?? '',
          space: tx.spaceName ?? '',   spaceId: tx.spaceId ?? '',
        } : {}),
        id: tx.paymentIntentId,
        transactionType: tx.transactionType ?? '',
        status: tx.status,
        customer: tx.customer ?? '',
        customerName: tx.customerName ?? '',
        gateway: tx.gateway ?? '',
        method: tx.paymentMethod ?? '',
        date: new Date(tx.eventDate),
        currency: tx.currency ?? '',
        charged: Number(tx.charge?.capturedAmount ?? tx.orderBreakdown?.netAmount ?? 0),
        paid: (tx.transactionType === 'Refund' ? -1 : 1) * Number(tx.amount ?? 0),
        fee: tx.charge?.gatewayFee != null ? Number(tx.charge.gatewayFee) : null,
        fxRate: tx.charge?.exchangeRate != null ? Number(tx.charge.exchangeRate) : null,
        contractPct: tx.charge?.contractPercentageApplied != null ? Number(tx.charge.contractPercentageApplied) : null,
        // Always in `currency` above — the one-currency reporting figure.
        netAmount: tx.charge?.netAmount != null ? Number(tx.charge.netAmount) : null,
        // Settled Amount is in ITS OWN currency, may differ from `currency` above —
        // kept as a separate column rather than embedded in the number, so the
        // amount stays usable for Excel math.
        merchantNet: tx.charge?.merchantNetAmount != null ? Number(tx.charge.merchantNetAmount) : null,
        merchantNetCurrency: tx.charge?.merchantNetCurrency ?? '',
        depositId: tx.charge?.depositId ?? '',
        refund: tx.refund?.reason ?? '',
      };

      const zebra = i % 2 === 1;
      columns.forEach((col) => {
        const cell = row.getCell(colIndex[col.key]);
        cell.value = values[col.key];
        cell.font = { size: 10, color: { argb: EXCEL_TEXT } };
        cell.alignment = { vertical: 'middle', horizontal: RIGHT_ALIGN_KEYS.includes(col.key) ? 'right' : 'left' };
        cell.border = {
          top: { style: 'thin', color: { argb: EXCEL_BORDER } }, bottom: { style: 'thin', color: { argb: EXCEL_BORDER } },
          left: { style: 'thin', color: { argb: EXCEL_BORDER } }, right: { style: 'thin', color: { argb: EXCEL_BORDER } },
        };
        if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_ZEBRA } };
      });

      row.getCell(colIndex.date).numFmt = 'mmm dd, yyyy hh:mm AM/PM';
      row.getCell(colIndex.charged).numFmt = '#,##0.00';
      row.getCell(colIndex.paid).numFmt = '#,##0.00;[Red]-#,##0.00';
      if (values.fee != null) row.getCell(colIndex.fee).numFmt = '#,##0.00';
      if (values.fxRate != null) row.getCell(colIndex.fxRate).numFmt = '0.0000';
      if (values.contractPct != null) row.getCell(colIndex.contractPct).numFmt = '0.00"%"';
      if (values.netAmount != null) row.getCell(colIndex.netAmount).numFmt = '#,##0.00';
      if (values.merchantNet != null) row.getCell(colIndex.merchantNet).numFmt = '#,##0.00';

      const statusCell = row.getCell(colIndex.status);
      const statusCfg = STATUS_CONFIG[values.status];
      statusCell.font = { bold: true, size: 10, color: { argb: statusCfg ? toArgb(statusCfg.color) : EXCEL_TEXT } };
      statusCell.alignment = { vertical: 'middle', horizontal: 'center' };

      row.height = 18;
    });

    sheet.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: colCount } };
    sheet.views = [{ state: 'frozen', ySplit: HEADER_ROW }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `transactions_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  // Export is capped to a trailing 6-month window — like a bank statement
  // export — so exporting can never attempt to pull the entire (potentially
  // huge) transaction history in one request.
  const EXPORT_MIN_DATE = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().slice(0, 10);
  }, []);
  const EXPORT_MAX_DATE = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const isExportRangeValid = Boolean(
    exportDateFrom && exportDateTo && exportDateFrom <= exportDateTo &&
    exportDateFrom >= EXPORT_MIN_DATE && exportDateTo <= EXPORT_MAX_DATE
  );

  // Spaces available for the export modal's space multi-select — mirrors
  // `availableSpaces` above but scoped to the export's own entity selection.
  const exportAvailableSpaces = useMemo(() => {
    if (!entities.length) return [];
    const pool = exportEntityIds.length
      ? entities.filter((e) => exportEntityIds.includes(e.id))
      : entities;
    return pool.flatMap((e) => e.spaces ?? []);
  }, [entities, exportEntityIds]);

  useEffect(() => {
    if (!exportSpaceIds.length) return;
    const availableIds = exportAvailableSpaces.map((s) => s.id);
    setExportSpaceIds((prev) => prev.filter((id) => availableIds.includes(id)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportAvailableSpaces]);

  // Opening the modal seeds it from whatever's already committed on the main
  // table (convenience) clamped into the exportable window — left blank if
  // the table has no date filter (or one outside the window), forcing a
  // deliberate choice before export is enabled. From here on the two filter
  // sets are independent: adjusting the export modal never touches the table.
  const openExportModal = () => {
    setExportStatuses(selectedStatuses);
    setExportGateways(selectedGateways);
    setExportTransactionTypes(selectedTransactionTypes);
    setExportEntityIds(selectedEntityIds);
    setExportSpaceIds(selectedSpaceIds);
    setExportDateFrom(committedDateRange.from && committedDateRange.from >= EXPORT_MIN_DATE ? committedDateRange.from : '');
    setExportDateTo(committedDateRange.to && committedDateRange.to <= EXPORT_MAX_DATE ? committedDateRange.to : '');
    setExportModalOpen(true);
  };

  const buildExportFilterParams = () => ({
    status:          exportStatuses.length         ? exportStatuses         : undefined,
    gateway:         exportGateways.length         ? exportGateways         : undefined,
    transactionType: exportTransactionTypes.length ? exportTransactionTypes : undefined,
    dateFrom:        exportDateFrom,
    dateTo:          exportDateTo,
    ...(showGlobalFilters
      ? (exportSpaceIds.length ? { spaceIds: exportSpaceIds }
         : exportEntityIds.length ? { entityIds: exportEntityIds }
         : {})
      : {}),
  });

  // Fetches every row matching the modal's own (6-month-bounded) filters.
  const handleConfirmExport = async () => {
    if (!isExportRangeValid || exporting) return;
    setExporting(true);
    try {
      const data = await fetchFn({
        page: 0,
        pageSize: MAX_EXPORT_ROWS,
        ...buildExportFilterParams(),
      });
      const allRows = Array.isArray(data) ? data : data?.items ?? [];
      if (allRows.length === 0) {
        toast.warning('No transactions match the selected filters.');
        return;
      }
      await downloadExcel(allRows);
      setExportModalOpen(false);
    } catch {
      // error shown globally via axios interceptor → toast
    } finally {
      setExporting(false);
    }
  };

  const colHead = { color: '#64748b', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', py: 1.75, bgcolor: '#f8fafc' };
  const colCell = { fontSize: 12, color: '#1e293b', py: 1.5, borderBottom: '1px solid #f1f5f9' };

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

      {/* ── Toolbar ─────────────────────────────────────── */}
      <Box sx={{ borderBottom: '1px solid #f1f5f9' }}>

        {/* Row 1: title + action buttons */}
        <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography fontWeight={700} fontSize={16} sx={{ color: '#1e293b' }}>
              <LocalText>Transaction Logs</LocalText>
            </Typography>
            <Typography fontSize={12} sx={{ color: '#94a3b8', mt: 0.25 }}>
              <LocalText>All charges, refunds, and pending payments across all portals</LocalText>
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={<LocalText>Reload data</LocalText>}>
              <IconButton size="small" onClick={fetchTransactions} sx={{ border: '1px solid #dde6f0', borderRadius: '10px', width: 36, height: 36, color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
                <RefreshIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={<LocalText>Reset all filters</LocalText>}>
              <IconButton size="small" onClick={resetFilters} sx={{ border: '1px solid #dde6f0', borderRadius: '10px', width: 36, height: 36, color: '#64748b', '&:hover': { bgcolor: '#fff1f2', color: '#dc2626', borderColor: '#fecaca' } }}>
                <FilterAltOffIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={<LocalText>Export transactions to Excel</LocalText>}>
              <Box
                component="button"
                onClick={openExportModal}
                disabled={exporting}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.75, height: 36, border: '1px solid #dde6f0', borderRadius: '10px', bgcolor: 'white', cursor: exporting ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, color: exporting ? '#cbd5e1' : '#0a326d', transition: 'all 0.15s', '&:hover:not(:disabled)': { bgcolor: '#eff6ff', borderColor: '#bfdbfe' } }}
              >
                {exporting ? <CircularProgress size={14} sx={{ color: '#cbd5e1' }} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                <LocalText>Export Excel</LocalText>
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {/* Filter rows */}
        <Box sx={{ px: 3, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* Row 1: Search + Date range — side by side on sm+ (unchanged); on mobile,
              Search stacks above the Date row, and From/To stay side by side within it
              (their text labels hide on mobile — the picker's own placeholder already
              says "Start/End date" — so both fit one row instead of each wrapping alone). */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: { sm: 'space-between' }, flexWrap: { sm: 'wrap' }, gap: 1.5 }}>
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: { xs: '100%', sm: 'auto' }, flex: { sm: '1 1 200px' }, maxWidth: { sm: 320 }, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13, '& fieldset': { borderColor: '#dde6f0' }, '&:hover fieldset': { borderColor: '#5383b3' }, '&.Mui-focused fieldset': { borderColor: '#0a326d', borderWidth: 2 } } }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography fontSize={11} fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' }, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  <LocalText>From</LocalText>
                </Typography>
                <CustomDatePicker
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => handleDateFromChange(e.target.value)}
                  placeholder="Start date"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography fontSize={11} fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' }, color: isDateRangeError ? '#ef4444' : '#94a3b8', whiteSpace: 'nowrap' }}>
                  <LocalText>To</LocalText>
                </Typography>
                <CustomDatePicker
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => handleDateToChange(e.target.value)}
                  placeholder="End date"
                  isError={isDateRangeError}
                />
              </Box>
              {isDateRangeError && (
                <Typography fontSize={11} sx={{ color: '#ef4444', whiteSpace: 'nowrap' }}>
                  <LocalText>Start date must be before end date</LocalText>
                </Typography>
              )}
            </Box>
          </Box>

          {/* Row 2: dropdown filters — one flex-wrap row on sm+ (unchanged from before).
              On mobile, Status/Gateway/Type form their own 2-column grid (the wrapper
              becomes `display: contents` on sm+, so it disappears and they rejoin the row
              exactly as today); Entity, Space and the active-filter chip stay outside that
              grid so they keep their own full-width row on mobile. */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: { sm: 'wrap' }, alignItems: { sm: 'center' }, gap: 1.5 }}>

            <Box sx={{ display: { xs: 'grid', sm: 'contents' }, gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <Select
              size="small"
              multiple
              displayEmpty
              value={selectedStatuses}
              onChange={(e) => setSelectedStatuses(e.target.value)}
              renderValue={(selected) =>
                selected.length === 0
                  ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Statuses</span>
                  : selected.join(', ')
              }
              sx={compactFilterSelectSx}
              MenuProps={dropdownMenuProps}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status} dense>
                  <Checkbox checked={selectedStatuses.includes(status)} size="small" sx={{ py: 0.25 }} />
                  <ListItemText primary={<LocalText>{status}</LocalText>} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              multiple
              displayEmpty
              value={selectedGateways}
              onChange={(e) => setSelectedGateways(e.target.value)}
              renderValue={(selected) =>
                selected.length === 0
                  ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Gateways</span>
                  : selected.join(', ')
              }
              sx={compactFilterSelectSx}
              MenuProps={dropdownMenuProps}
            >
              {GATEWAY_OPTIONS.map((gw) => (
                <MenuItem key={gw} value={gw} dense>
                  <Checkbox checked={selectedGateways.includes(gw)} size="small" sx={{ py: 0.25 }} />
                  <ListItemText primary={gw} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              multiple
              displayEmpty
              value={selectedTransactionTypes}
              onChange={(e) => setSelectedTransactionTypes(e.target.value)}
              renderValue={(selected) =>
                selected.length === 0
                  ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Types</span>
                  : selected.join(', ')
              }
              sx={compactFilterSelectSx}
              MenuProps={dropdownMenuProps}
            >
              {TXN_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type} dense>
                  <Checkbox checked={selectedTransactionTypes.includes(type)} size="small" sx={{ py: 0.25 }} />
                  <ListItemText primary={<LocalText>{type}</LocalText>} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>
            </Box>

            {showGlobalFilters && (
              <>
                <Select
                  size="small"
                  multiple
                  displayEmpty
                  value={selectedEntityIds}
                  onChange={(e) => setSelectedEntityIds(e.target.value)}
                  disabled={loadingEntities}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Entities</span>
                      : entities.filter((e) => selected.includes(e.id)).map((e) => e.name).join(', ')
                  }
                  sx={entitySpaceSelectSx}
                  MenuProps={entitySpaceMenuProps}
                >
                  {entities.map((entity) => (
                    <MenuItem key={entity.id} value={entity.id} dense>
                      <Checkbox checked={selectedEntityIds.includes(entity.id)} size="small" sx={{ py: 0.25 }} />
                      <ListItemText primary={entity.name} primaryTypographyProps={{ fontSize: 13 }} />
                    </MenuItem>
                  ))}
                  {!loadingEntities && entities.length === 0 && (
                    <MenuItem disabled dense>
                      <ListItemText primary="No entities available" primaryTypographyProps={{ fontSize: 13, color: '#94a3b8' }} />
                    </MenuItem>
                  )}
                </Select>

                <Select
                  size="small"
                  multiple
                  displayEmpty
                  value={selectedSpaceIds}
                  onChange={(e) => setSelectedSpaceIds(e.target.value)}
                  disabled={loadingEntities || availableSpaces.length === 0}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Spaces</span>
                      : availableSpaces.filter((s) => selected.includes(s.id)).map((s) => s.name).join(', ')
                  }
                  sx={entitySpaceSelectSx}
                  MenuProps={entitySpaceMenuProps}
                >
                  {availableSpaces.map((space) => (
                    <MenuItem key={space.id} value={space.id} dense>
                      <Checkbox checked={selectedSpaceIds.includes(space.id)} size="small" sx={{ py: 0.25 }} />
                      <ListItemText primary={space.name} primaryTypographyProps={{ fontSize: 13 }} />
                    </MenuItem>
                  ))}
                  {availableSpaces.length === 0 && (
                    <MenuItem disabled dense>
                      <ListItemText
                        primary={selectedEntityIds.length ? 'No spaces in selected entities' : 'Select an entity first'}
                        primaryTypographyProps={{ fontSize: 13, color: '#94a3b8' }}
                      />
                    </MenuItem>
                  )}
                </Select>

                {(selectedEntityIds.length > 0 || selectedSpaceIds.length > 0) && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '999px', bgcolor: '#eff6ff', fontSize: 11, fontWeight: 700, color: '#0a326d' }}>
                    {selectedSpaceIds.length > 0
                      ? `${selectedSpaceIds.length} ${selectedSpaceIds.length === 1 ? 'space' : 'spaces'}`
                      : `${selectedEntityIds.length} ${selectedEntityIds.length === 1 ? 'entity' : 'entities'}`
                    }
                  </Box>
                )}
              </>
            )}
          </Box>

        </Box>

      </Box>

      {/* ── Table ───────────────────────────────────────── */}
      <Box sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress sx={{ color: '#0a326d' }} />
          </Box>
        ) : (
          <Table sx={{ minWidth: { xs: 900, md: showGlobalFilters ? 1300 : 1100 } }}>
            <TableHead>
              <TableRow>
                {(showGlobalFilters ? [...GLOBAL_COLUMNS, ...BASE_COLUMNS] : BASE_COLUMNS).map(col => (
                  <TableCell key={col.key} sx={{ ...colHead, textAlign: col.align }}>
                    <LocalText>{col.label}</LocalText>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={(showGlobalFilters ? GLOBAL_COLUMNS.length : 0) + BASE_COLUMNS.length}
                    sx={{ textAlign: 'center', py: 6, color: '#94a3b8', fontSize: 13 }}
                  >
                    <LocalText>No transactions found.</LocalText>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => (
                  // paymentIntentId alone isn't unique here — a partially/fully refunded intent
                  // produces one "Succeeded" row (from the charge) PLUS one row per refund event,
                  // all sharing the same paymentIntentId. status+eventDate distinguishes them.
                  <TableRow key={`${tx.paymentIntentId}-${tx.charge?.id ?? 'none'}-${tx.status}-${tx.eventDate}`} sx={{ '&:hover': { bgcolor: '#fafbfc' }, '&:last-child td': { border: 0 } }}>

                    {/* Entity — global view only */}
                    {showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} fontWeight={600} sx={{ color: '#1e293b' }}>
                          {tx.entityName ?? '—'}
                        </Typography>
                      </TableCell>
                    )}

                    {/* Entity ID — global view only */}
                    {showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                          {tx.entityId ?? '—'}
                        </Typography>
                      </TableCell>
                    )}

                    {/* Space — global view only */}
                    {showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} sx={{ color: '#475569' }}>
                          {tx.spaceName ?? '—'}
                        </Typography>
                      </TableCell>
                    )}

                    {/* Space ID — global view only */}
                    {showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                          {tx.spaceId ?? '—'}
                        </Typography>
                      </TableCell>
                    )}

                    {/* Payment Intent ID */}
                    <TableCell sx={colCell}>
                      <Typography fontSize={11} fontWeight={700} sx={{ color: '#1e293b', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {tx.paymentIntentId}
                      </Typography>
                    </TableCell>

                    {/* Transaction Type */}
                    <TableCell sx={colCell}>
                      <TxnTypeBadge type={tx.transactionType} />
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={colCell}>
                      <StatusBadge status={tx.status} />
                    </TableCell>

                    {/* Customer Email / ID */}
                    <TableCell sx={colCell}>
                      <Typography fontSize={12} sx={{ color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {tx.customer ?? '—'}
                      </Typography>
                    </TableCell>

                    {/* Customer Name */}
                    <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                      <Typography fontSize={12} sx={{ color: '#475569' }}>
                        {tx.customerName ?? '—'}
                      </Typography>
                    </TableCell>

                    {/* Gateway */}
                    <TableCell sx={colCell}>
                      {tx.gateway ? <GatewayBadge gateway={tx.gateway} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                    </TableCell>

                    {/* Payment Method */}
                    <TableCell sx={{ ...colCell, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {tx.paymentMethod ?? '—'}
                    </TableCell>

                    {/* Date */}
                    <TableCell sx={{ ...colCell, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {formatDate(tx.eventDate)}
                    </TableCell>

                    {/* Currency */}
                    <TableCell sx={{ ...colCell, color: '#64748b' }}>
                      {tx.currency ?? '—'}
                    </TableCell>

                    {/* Charged Amt */}
                    <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Typography fontSize={13} sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                        {formatAmount(tx.charge?.capturedAmount ?? tx.orderBreakdown?.netAmount, tx.currency)}
                      </Typography>
                    </TableCell>

                    {/* Paid / Refund Amt */}
                    <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Typography
                        fontSize={13}
                        fontWeight={700}
                        sx={{ color: isRefundedStatus(tx.status) ? '#7c3aed' : '#15803d', whiteSpace: 'nowrap' }}
                      >
                        {isRefundedStatus(tx.status) ? '−' : ''}{formatAmount(tx.amount, tx.currency)}
                      </Typography>
                    </TableCell>

                    {/* Gateway Fee */}
                    <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {tx.charge?.gatewayFee != null ? formatAmount(tx.charge.gatewayFee, tx.currency) : <span style={{ color: '#94a3b8' }}>—</span>}
                    </TableCell>

                    {/* FX Rate — only present on cross-currency charges */}
                    <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {tx.charge?.exchangeRate != null ? Number(tx.charge.exchangeRate).toFixed(4) : <span style={{ color: '#94a3b8' }}>—</span>}
                    </TableCell>

                    {/* Contract % — snapshot of the space's configured contract percentage at capture time */}
                    <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {tx.charge?.contractPercentageApplied != null ? `${Number(tx.charge.contractPercentageApplied).toFixed(2)}%` : <span style={{ color: '#94a3b8' }}>—</span>}
                    </TableCell>

                    {/* Net Amount — settlement amount converted into the transaction's OWN
                        currency (tx.currency), so this column is always one-currency and
                        summable. Equal to Settled Amount when no conversion happened. */}
                    <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {tx.charge?.netAmount != null
                        ? formatAmount(tx.charge.netAmount, tx.currency)
                        : <span style={{ color: '#94a3b8' }}>—</span>}
                    </TableCell>

                    {/* Settled Amount — the actual amount credited to the merchant balance,
                        after fee + conversion. Uses ITS OWN currency (merchantNetCurrency),
                        which can legitimately differ from the row's Currency column — never
                        format this with tx.currency. This is the ground-truth PSP figure. */}
                    <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {tx.charge?.merchantNetAmount != null
                        ? formatAmount(tx.charge.merchantNetAmount, tx.charge.merchantNetCurrency ?? tx.currency)
                        : <span style={{ color: '#94a3b8' }}>—</span>}
                    </TableCell>

                    {/* Deposit ID — manual grouping tag the space finance user assigns after
                        reconciling this charge against a real bank deposit. */}
                    <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                      {tx.charge?.depositId
                        ? (
                          <Box sx={{ display: 'inline-block', px: 1, py: 0.3, borderRadius: '6px', bgcolor: '#f1f5f9', fontSize: 11, fontWeight: 700, color: '#334155', fontFamily: 'monospace' }}>
                            {tx.charge.depositId}
                          </Box>
                        )
                        : <span style={{ color: '#94a3b8' }}>—</span>}
                    </TableCell>

                    {/* Refund Reason */}
                    <TableCell sx={{ ...colCell, color: '#64748b', maxWidth: 180 }}>
                      {tx.refund?.reason ?? '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* ── Pagination ──────────────────────────────────── */}
      {!loading && (
        <Box sx={{ p: 3, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize={12} sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                <LocalText>Rows per page</LocalText>
              </Typography>
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value)}
                sx={{ ...selectFieldSx, fontSize: 12, minWidth: 68 }}
                MenuProps={dropdownMenuProps}
              >
                {PAGE_SIZE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </Box>

            <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
              <LocalText>Showing</LocalText>{' '}
              <Box component="span" fontWeight={700} sx={{ color: '#1e293b' }}>
                {totalCount === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalCount)}
              </Box>{' '}
              <LocalText>of</LocalText>{' '}
              <Box component="span" fontWeight={700} sx={{ color: '#1e293b' }}>{totalCount}</Box>{' '}
              <LocalText>transactions</LocalText>
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.75}>
            <Box component="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              sx={{ px: 2, py: 0.75, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', fontSize: 12, fontWeight: 500, color: page === 0 ? '#cbd5e1' : '#64748b', cursor: page === 0 ? 'not-allowed' : 'pointer', '&:hover:not(:disabled)': { bgcolor: '#f8fafc' } }}>
              <LocalText>Previous</LocalText>
            </Box>

            {Array.from({ length: totalPages }, (_, i) => (
              <Box key={i} component="button" onClick={() => setPage(i)}
                sx={{ minWidth: 32, py: 0.75, border: '1px solid', borderRadius: '8px', fontSize: 12, fontWeight: 700, cursor: 'pointer', ...(i === page ? { bgcolor: '#0a326d', color: 'white', borderColor: '#0a326d' } : { bgcolor: 'white', color: '#64748b', borderColor: '#dde6f0', '&:hover': { bgcolor: '#f8fafc' } }) }}>
                {i + 1}
              </Box>
            ))}

            <Box component="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              sx={{ px: 2, py: 0.75, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', fontSize: 12, fontWeight: 500, color: page >= totalPages - 1 ? '#cbd5e1' : '#64748b', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', '&:hover:not(:disabled)': { bgcolor: '#f8fafc' } }}>
              <LocalText>Next</LocalText>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Export filters — independent of the main table's live filters ── */}
      <Dialog open={exportModalOpen} onClose={() => !exporting && setExportModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>
          <LocalText>Export Transactions</LocalText>
        </DialogTitle>
        <DialogContent dividers>
          <Typography fontSize={12} sx={{ color: '#64748b', mb: 2 }}>
            <LocalText>Choose a date range (up to 6 months) to export. These filters only affect the export — they won't change the table behind this dialog.</LocalText>
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography fontSize={11} fontWeight={700} sx={{ color: '#334155', whiteSpace: 'nowrap' }}>
                <LocalText>From</LocalText> *
              </Typography>
              <CustomDatePicker
                value={exportDateFrom}
                min={EXPORT_MIN_DATE}
                max={exportDateTo || EXPORT_MAX_DATE}
                onChange={(e) => setExportDateFrom(e.target.value)}
                placeholder="Start date"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography fontSize={11} fontWeight={700} sx={{ color: '#334155', whiteSpace: 'nowrap' }}>
                <LocalText>To</LocalText> *
              </Typography>
              <CustomDatePicker
                value={exportDateTo}
                min={exportDateFrom || EXPORT_MIN_DATE}
                max={EXPORT_MAX_DATE}
                onChange={(e) => setExportDateTo(e.target.value)}
                placeholder="End date"
              />
            </Box>
          </Box>

          {!isExportRangeValid && (
            <Typography fontSize={11.5} sx={{ color: '#ef4444', mb: 2 }}>
              <LocalText>Select a From and To date within the last 6 months to enable export.</LocalText>
            </Typography>
          )}

          <Typography fontSize={11} fontWeight={700} sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', mt: 2, mb: 1 }}>
            <LocalText>Optional filters</LocalText>
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Select
              size="small"
              multiple
              displayEmpty
              value={exportStatuses}
              onChange={(e) => setExportStatuses(e.target.value)}
              renderValue={(selected) =>
                selected.length === 0
                  ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Statuses</span>
                  : selected.join(', ')
              }
              sx={multiSelectSx}
              MenuProps={dropdownMenuProps}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status} dense>
                  <Checkbox checked={exportStatuses.includes(status)} size="small" sx={{ py: 0.25 }} />
                  <ListItemText primary={<LocalText>{status}</LocalText>} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              multiple
              displayEmpty
              value={exportGateways}
              onChange={(e) => setExportGateways(e.target.value)}
              renderValue={(selected) =>
                selected.length === 0
                  ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Gateways</span>
                  : selected.join(', ')
              }
              sx={multiSelectSx}
              MenuProps={dropdownMenuProps}
            >
              {GATEWAY_OPTIONS.map((gw) => (
                <MenuItem key={gw} value={gw} dense>
                  <Checkbox checked={exportGateways.includes(gw)} size="small" sx={{ py: 0.25 }} />
                  <ListItemText primary={gw} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              multiple
              displayEmpty
              value={exportTransactionTypes}
              onChange={(e) => setExportTransactionTypes(e.target.value)}
              renderValue={(selected) =>
                selected.length === 0
                  ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Types</span>
                  : selected.join(', ')
              }
              sx={multiSelectSx}
              MenuProps={dropdownMenuProps}
            >
              {TXN_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type} dense>
                  <Checkbox checked={exportTransactionTypes.includes(type)} size="small" sx={{ py: 0.25 }} />
                  <ListItemText primary={<LocalText>{type}</LocalText>} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>

            {showGlobalFilters && (
              <>
                <Select
                  size="small"
                  multiple
                  displayEmpty
                  value={exportEntityIds}
                  onChange={(e) => setExportEntityIds(e.target.value)}
                  disabled={loadingEntities}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Entities</span>
                      : entities.filter((e) => selected.includes(e.id)).map((e) => e.name).join(', ')
                  }
                  sx={entitySpaceSelectSx}
                  MenuProps={entitySpaceMenuProps}
                >
                  {entities.map((entity) => (
                    <MenuItem key={entity.id} value={entity.id} dense>
                      <Checkbox checked={exportEntityIds.includes(entity.id)} size="small" sx={{ py: 0.25 }} />
                      <ListItemText primary={entity.name} primaryTypographyProps={{ fontSize: 13 }} />
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  size="small"
                  multiple
                  displayEmpty
                  value={exportSpaceIds}
                  onChange={(e) => setExportSpaceIds(e.target.value)}
                  disabled={loadingEntities || exportAvailableSpaces.length === 0}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Spaces</span>
                      : exportAvailableSpaces.filter((s) => selected.includes(s.id)).map((s) => s.name).join(', ')
                  }
                  sx={entitySpaceSelectSx}
                  MenuProps={entitySpaceMenuProps}
                >
                  {exportAvailableSpaces.map((space) => (
                    <MenuItem key={space.id} value={space.id} dense>
                      <Checkbox checked={exportSpaceIds.includes(space.id)} size="small" sx={{ py: 0.25 }} />
                      <ListItemText primary={space.name} primaryTypographyProps={{ fontSize: 13 }} />
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box
            component="button"
            onClick={() => setExportModalOpen(false)}
            disabled={exporting}
            sx={{ px: 2, height: 36, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', cursor: exporting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#64748b' }}
          >
            <LocalText>Cancel</LocalText>
          </Box>
          <Box
            component="button"
            onClick={handleConfirmExport}
            disabled={!isExportRangeValid || exporting}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2.25, height: 36, border: 'none', borderRadius: '8px', bgcolor: '#0a326d', cursor: (!isExportRangeValid || exporting) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, color: 'white', opacity: (!isExportRangeValid || exporting) ? 0.5 : 1 }}
          >
            {exporting ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <LocalText>Export</LocalText>}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionTable;
